// kiloGRAM i18n — Telegram language_code → app language.
// Supported: ru, en, pt, es, kk. Fallback: en.

import { retrieveLaunchParams } from '@telegram-apps/sdk';

export type Lang = 'ru' | 'en' | 'pt' | 'es' | 'kk';
const SUPPORTED: Lang[] = ['ru', 'en', 'pt', 'es', 'kk'];

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function telegramCode(): string | null {
  try {
    const code = retrieveLaunchParams().tgWebAppData?.user?.language_code;
    if (code) return code;
  } catch { /* not inside Telegram */ }
  try {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const data = hash.get('tgWebAppData');
    if (data) {
      const u = JSON.parse(decodeURIComponent(data)).user;
      if (u?.language_code) return u.language_code as string;
    }
  } catch { /* ignore */ }
  return null;
}

const LANG_KEY = 'kg_lang';

function detectLang(): Lang {
  // Manual pick always wins (iOS has no reliable auto signal).
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (saved) return normalize(saved);
  } catch { /* private mode */ }
  // iOS: WebView locale is the iPhone system language, not Telegram's UI
  // language — prefer initData's user.language_code (fresh client value).
  // Android/desktop: navigator.language follows Telegram UI instantly and
  // initData's language_code can lag behind.
  if (isIOS()) {
    const code = telegramCode();
    if (code) return normalize(code);
  }
  return normalize(navigator.language || 'en');
}

/** Persisted manual language choice; takes effect on reload. */
export function setLang(l: Lang): void {
  try { localStorage.setItem(LANG_KEY, l); } catch { /* ignore */ }
}

export const LANG_NAMES: Record<Lang, string> = {
  en: 'English', ru: 'Русский', pt: 'Português', es: 'Español', kk: 'Қазақша',
};

function normalize(code: string): Lang {
  const base = code.slice(0, 2).toLowerCase() as Lang;
  return SUPPORTED.includes(base) ? base : 'en';
}

export const lang: Lang = detectLang();

type Dict = Record<string, string>;
const en: Dict = {
  round: 'Round',
  jackpot_label: 'Round bank',
  deposits_one: 'deposit',
  deposits_many: 'deposits',
  next_round_in: 'Next round in',
  round_done: 'Round #{n} is over',
  round_done_wait: 'Payout in progress…',
  round_ends_in: 'Round ends in',
  invite_cta: 'Invite a friend — halve your wait',
  invite_copied: 'Link copied — send it to your friend',
  sending: 'Sending…',
  deposit_btn: 'Send {amount} GRAM',
  connect_wallet: 'Connect your wallet',
  connect_wallet_sub: 'to take part',
  round_bets: 'Round deposits',
  you: 'you',
  last_winner: 'Last winner · round #{n}',
  winners_tab: 'Winners',
  leaders_tab: 'Top players',
  show_all: 'Show all',
  empty: 'Nothing yet',
  wins_one: 'win',
  wins_many: 'wins',
  rules_title: 'How it works',
  rules_h1: 'Mechanics',
  rules_1: 'Deposit — exactly {amount} GRAM.',
  rules_2: 'Every deposit restarts the timer — up to 30 minutes, depends on activity.',
  rules_3: 'The last depositor takes 75% of the bank.',
  rules_4: 'Payout: 75% to the winner, 15% to the developer, 10% seeds the next round.',
  rules_5: 'After depositing, a personal 30-minute cooldown; invite a newcomer to halve it.',
  rules_6: 'Winnings are paid by the TON smart contract straight to the winner\u2019s wallet.',
  rules_7: 'Rounds are separated by a 5-minute pause.',
  rules_h2: 'Confidence',
  safe_1: 'All rules and mechanics are permanently embedded in the TON smart contract.',
  safe_2: 'Once on-chain, the code cannot be changed — by anyone, ever.',
  safe_3: 'The contract runs independently of any servers, as long as TON lives.',
  safe_4: 'No external control or influence — everything runs on the TON blockchain.',
  safe_5: 'The source code and every transaction are open for anyone to verify on',
  safe_6: 'Even if the app and the bot disappear, the game keeps running by its rules.',
  safe_7: 'Blockchain transactions are fast and irreversible — be careful :)',
  splash_tag: 'last depositor takes it all',
  splash_in: 'Starting in',
  splash_connect: 'Connect wallet',
  splash_note: 'Connect to take part.',
  splash_note2: 'A positive GRAM balance is required',
  splash_bot: 'Get notified at launch',
  loading: 'Loading…',
  copy_prompt: 'Copy this link for a friend:',
  sound: 'Sound',
  sound_pick_file: 'Choose file…',
};

const ru: Dict = {
  round: 'Раунд',
  jackpot_label: 'Банк раунда',
  deposits_one: 'депозит',
  deposits_many: 'депозитов',
  next_round_in: 'Следующий раунд через',
  round_done: 'Раунд №{n} состоялся',
  round_done_wait: 'Подсчёт и выплата…',
  round_ends_in: 'До конца раунда',
  invite_cta: 'Пригласить друга — минус 50% ожидания',
  invite_copied: 'Ссылка скопирована — отправьте другу',
  sending: 'Отправка…',
  deposit_btn: 'Отправить {amount} GRAM',
  connect_wallet: 'Подключите кошелёк',
  connect_wallet_sub: 'чтобы принять участие',
  round_bets: 'Ставки раунда',
  you: 'вы',
  last_winner: 'Последний победитель · раунд №{n}',
  winners_tab: 'Победители',
  leaders_tab: 'Топ игроков',
  show_all: 'Показать все',
  empty: 'Пока пусто',
  wins_one: 'победа',
  wins_many: 'побед',
  rules_title: 'Как это работает',
  rules_h1: 'Механика',
  rules_1: 'Ставка — ровно {amount} GRAM.',
  rules_2: 'Каждая ставка перезапускает таймер — до 30 минут, зависит от активности.',
  rules_3: 'Кто поставил последним и больше не было ставок — забирает 75% банка.',
  rules_4: 'Выигрыш: 75% победителю, 15% — разработчику, 10% — старт банка следующего раунда.',
  rules_5: 'После ставки персональная пауза 30 минут, пригласите новичка — она сократится вдвое.',
  rules_6: 'Выигрыш выплачивает смарт-контракт TON автоматически в кошелёк победителя.',
  rules_7: 'Между раундами — пауза 5 минут.',
  rules_h2: 'Уверенность',
  safe_1: 'Все правила и механики приложения навсегда зашиты в смарт-контракт TON.',
  safe_2: 'После загрузки в блокчейн исходный код не может быть изменён — никем и никогда.',
  safe_3: 'Загруженный код работает независимо от любых серверов, пока жив блокчейн TON.',
  safe_4: 'Никакого внешнего управления и влияния — всё работает на блокчейне TON.',
  safe_5: 'Исходный код и все транзакции открыты каждому для проверки через',
  safe_6: 'Даже если приложение и бот исчезнут, игра продолжит работать согласно механикам.',
  safe_7: 'Блокчейн-транзакции Gram быстры и необратимы — осторожнее в своих желаниях :)',
  splash_tag: 'последний забирает всё',
  splash_in: 'Старт через',
  splash_connect: 'Подключить кошелёк',
  splash_note: 'Подключите чтобы принять участие.',
  splash_note2: 'Потребуется положительный баланс в GRAM',
  splash_bot: 'Получить пуш при старте',
  loading: 'Загрузка…',
  copy_prompt: 'Скопируйте ссылку для друга:',
  sound: 'Звук',
  sound_pick_file: 'Файл с устройства…',
};

const pt: Dict = {
  round: 'Rodada',
  jackpot_label: 'Banca da rodada',
  deposits_one: 'depósito',
  deposits_many: 'depósitos',
  next_round_in: 'Próxima rodada em',
  round_done: 'Rodada #{n} encerrada',
  round_done_wait: 'Calculando o pagamento…',
  round_ends_in: 'Fim da rodada em',
  invite_cta: 'Convide um amigo — reduza sua espera em 50%',
  invite_copied: 'Link copiado — envie ao seu amigo',
  sending: 'Enviando…',
  deposit_btn: 'Enviar {amount} GRAM',
  connect_wallet: 'Conecte sua carteira',
  connect_wallet_sub: 'para participar',
  round_bets: 'Apostas da rodada',
  you: 'você',
  last_winner: 'Último vencedor · rodada #{n}',
  winners_tab: 'Vencedores',
  leaders_tab: 'Melhores jogadores',
  show_all: 'Mostrar tudo',
  empty: 'Nada ainda',
  wins_one: 'vitória',
  wins_many: 'vitórias',
  rules_title: 'Como funciona',
  rules_h1: 'Mecânica',
  rules_1: 'Aposta — exatamente {amount} GRAM.',
  rules_2: 'Cada aposta reinicia o cronômetro — até 30 minutos, depende da atividade.',
  rules_3: 'Quem apostar por último leva 75% da banca.',
  rules_4: 'Prêmio: 75% ao vencedor, 15% ao desenvolvedor, 10% inicia a próxima rodada.',
  rules_5: 'Após apostar, pausa pessoal de 30 minutos; convide um novato para reduzi-la pela metade.',
  rules_6: 'O prêmio é pago pelo contrato inteligente TON direto na carteira do vencedor.',
  rules_7: 'Entre rodadas — pausa de 5 minutos.',
  rules_h2: 'Confiança',
  safe_1: 'Todas as regras e mecânicas do app estão gravadas para sempre no contrato inteligente TON.',
  safe_2: 'Depois de enviado à blockchain, o código não pode ser alterado — por ninguém, nunca.',
  safe_3: 'O código funciona independente de servidores, enquanto a TON existir.',
  safe_4: 'Sem controle ou influência externa — tudo roda na blockchain TON.',
  safe_5: 'O código-fonte e todas as transações são abertos para verificação via',
  safe_6: 'Mesmo que o app e o bot desapareçam, o jogo continua seguindo suas mecânicas.',
  safe_7: 'Transações em GRAM são rápidas e irreversíveis — cuidado com seus desejos :)',
  splash_tag: 'o último leva tudo',
  splash_in: 'Começa em',
  splash_connect: 'Conectar carteira',
  splash_note: 'Conecte-se para participar.',
  splash_note2: 'Será necessário saldo positivo em GRAM',
  splash_bot: 'Receber aviso no lançamento',
  loading: 'Carregando…',
  copy_prompt: 'Copie este link para um amigo:',
  sound: 'Som',
  sound_pick_file: 'Ficheiro do dispositivo…',
};

const es: Dict = {
  round: 'Ronda',
  jackpot_label: 'Bote de la ronda',
  deposits_one: 'depósito',
  deposits_many: 'depósitos',
  next_round_in: 'Siguiente ronda en',
  round_done: 'Ronda #{n} finalizada',
  round_done_wait: 'Calculando el pago…',
  round_ends_in: 'La ronda termina en',
  invite_cta: 'Invita a un amigo — reduce tu espera a la mitad',
  invite_copied: 'Enlace copiado — envíaselo a tu amigo',
  sending: 'Enviando…',
  deposit_btn: 'Enviar {amount} GRAM',
  connect_wallet: 'Conecta tu billetera',
  connect_wallet_sub: 'para participar',
  round_bets: 'Apuestas de la ronda',
  you: 'tú',
  last_winner: 'Último ganador · ronda #{n}',
  winners_tab: 'Ganadores',
  leaders_tab: 'Mejores jugadores',
  show_all: 'Ver todo',
  empty: 'Vacío por ahora',
  wins_one: 'victoria',
  wins_many: 'victorias',
  rules_title: 'Cómo funciona',
  rules_h1: 'Mecánica',
  rules_1: 'Apuesta — exactamente {amount} GRAM.',
  rules_2: 'Cada apuesta reinicia el temporizador — hasta 30 minutos, depende de la actividad.',
  rules_3: 'El último en apostar se lleva el 75% del bote.',
  rules_4: 'Premio: 75% al ganador, 15% al desarrollador, 10% inicia la siguiente ronda.',
  rules_5: 'Tras apostar, pausa personal de 30 minutos; invita a un novato para reducirla a la mitad.',
  rules_6: 'El contrato inteligente TON paga el premio automáticamente a la billetera del ganador.',
  rules_7: 'Entre rondas — pausa de 5 minutos.',
  rules_h2: 'Confianza',
  safe_1: 'Todas las reglas y mecánicas están grabadas para siempre en el contrato inteligente TON.',
  safe_2: 'Una vez en la blockchain, el código no puede ser modificado — por nadie, nunca.',
  safe_3: 'El código funciona sin depender de servidores, mientras viva TON.',
  safe_4: 'Sin control ni influencia externos — todo funciona en la blockchain TON.',
  safe_5: 'El código fuente y todas las transacciones están abiertos para verificar en',
  safe_6: 'Aunque la app y el bot desaparezcan, el juego seguirá funcionando según sus mecánicas.',
  safe_7: 'Las transacciones en GRAM son rápidas e irreversibles — cuidado con tus deseos :)',
  splash_tag: 'el último se lo lleva todo',
  splash_in: 'Empieza en',
  splash_connect: 'Conectar billetera',
  splash_note: 'Conéctate para participar.',
  splash_note2: 'Necesitarás saldo positivo en GRAM',
  splash_bot: 'Recibir aviso al lanzar',
  loading: 'Cargando…',
  copy_prompt: 'Copia este enlace para un amigo:',
  sound: 'Sonido',
  sound_pick_file: 'Archivo del dispositivo…',
};

const kk: Dict = {
  round: 'Раунд',
  jackpot_label: 'Раунд банкі',
  deposits_one: 'депозит',
  deposits_many: 'депозит',
  next_round_in: 'Келесі раунд',
  round_done: 'Раунд №{n} аяқталды',
  round_done_wait: 'Төлем есептелуде…',
  round_ends_in: 'Раунд аяқталуына',
  invite_cta: 'Досыңды шақыр — күту уақытың 50% азаяды',
  invite_copied: 'Сілтеме көшірілді — досыңа жібер',
  sending: 'Жіберілуде…',
  deposit_btn: '{amount} GRAM жіберу',
  connect_wallet: 'Әмиянды қосыңыз',
  connect_wallet_sub: 'қатысу үшін',
  round_bets: 'Раунд ставкалары',
  you: 'сіз',
  last_winner: 'Соңғы жеңімпаз · раунд №{n}',
  winners_tab: 'Жеңімпаздар',
  leaders_tab: 'Топ ойыншылар',
  show_all: 'Барлығын көрсету',
  empty: 'Әзірге бос',
  wins_one: 'жеңіс',
  wins_many: 'жеңіс',
  rules_title: 'Бұл қалай жұмыс істейді',
  rules_h1: 'Механика',
  rules_1: 'Ставка — дәл {amount} GRAM.',
  rules_2: 'Әр ставка таймерді қайта іске қосады — 30 минутқа дейін, белсенділікке байланысты.',
  rules_3: 'Соңғы ставка қойған банктың 75%-ын алады.',
  rules_4: 'Ұтыс: 75% жеңімпазге, 15% — әзірлеушіге, 10% — келесі раунд банкісіне.',
  rules_5: 'Ставкадан кейін жеке 30 минуттық үзіліс; жаңа ойыншы шақырсаңыз — екі есе қысқарады.',
  rules_6: 'Ұтысты TON смарт-келісімі жеңімпаз әмиянына автоматты түрде төлейді.',
  rules_7: 'Раундтар арасында — 5 минуттық үзіліс.',
  rules_h2: 'Сенімділік',
  safe_1: 'Барлық ереже мен механика TON смарт-келісіміне мәңгілікке енгізілген.',
  safe_2: 'Блокчейнге жүктелгеннен кейін кодты ешкім, ешқашан өзгерте алмайды.',
  safe_3: 'Код кез келген серверлерден тәуелсіз жұмыс істейді, TON тірі болғанша.',
  safe_4: 'Сыртқы басқару мен ықпал жоқ — бәрі TON блокчейнінде жүреді.',
  safe_5: 'Бастапқы код пен барлық транзакциялар кез келгенге тексеру үшін ашық —',
  safe_6: 'Қолданба мен бот жоғалса да, ойын механикасы бойынша жұмысын жалғастырады.',
  safe_7: 'GRAM блокчейн-транзакциялары жылдам әрі қайтымсыз — тілектеріңізге сақ болыңыз :)',
  splash_tag: 'соңғысы бәрін алады',
  splash_in: 'Басталуына',
  splash_connect: 'Әмиянды қосу',
  splash_note: 'Қатысу үшін қосылыңыз.',
  splash_note2: 'GRAM валютасында оң баланс қажет',
  splash_bot: 'Басталғанда хабарландыру алу',
  loading: 'Жүктелуде…',
  copy_prompt: 'Досыңызға арналған сілтемені көшіріңіз:',
  sound: 'Дыбыс',
  sound_pick_file: 'Құрылғыдан файл таңдау…',
};

const dicts: Record<Lang, Dict> = { en, ru, pt, es, kk };

/** Translate a key, interpolating {name} params. Falls back to English. */
export function t(key: keyof typeof en, params?: Record<string, string | number>): string {
  let s = dicts[lang][key] ?? en[key] ?? key;
  if (params) {
    for (const [k, v] of Object.entries(params)) s = s.replaceAll(`{${k}}`, String(v));
  }
  return s;
}

/** Plural for counters: Russian needs three forms, others two. */
const RU_FORMS: Record<string, [string, string, string]> = {
  deposits: ['депозит', 'депозита', 'депозитов'],
  wins: ['победа', 'победы', 'побед'],
};

export function plural(n: number, key: 'deposits' | 'wins'): string {
  if (lang === 'ru') {
    const f = RU_FORMS[key];
    return n % 10 === 1 && n % 100 !== 11 ? f[0]
      : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? f[1] : f[2];
  }
  return t(`${key}_${n === 1 ? 'one' : 'many'}` as keyof typeof en);
}
